import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { X, Mail, Lock, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

export const AuthModal = () => {
  const { t } = useTranslation();
  const { authMode, setAuthMode, closeAuthModal, login, register, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (authMode === 'login') {
        await login(formData.email, formData.password);
        toast.success(t('auth.loginSuccess'));
      } else {
        await register(formData.name, formData.email, formData.password, 'retail');
        toast.success(t('auth.registerSuccess'));
      }
    } catch (error) {
      console.error('Auth error:', error);
      const message = error.response?.data?.detail 
        || error.message 
        || t('auth.networkError');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="auth-modal" onClick={closeAuthModal}>
      <div className="relative w-full max-w-md mx-4 bg-brand-black border border-brand-gold/30 rounded-none p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Gold accent line at top */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-brand-gold" />
        
        {/* Close button */}
        <button 
          onClick={closeAuthModal}
          className="absolute top-4 right-4 text-brand-white/50 hover:text-brand-gold transition-colors"
          data-testid="close-auth-modal"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img 
            src="/logo.png" 
            alt="ALINA ELEGANCE" 
            className="h-24 w-auto"
          />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="font-heading text-3xl font-bold text-brand-gold mb-2 tracking-wide">
            {authMode === 'login' ? t('auth.login') : t('auth.register')}
          </h2>
          <p className="text-brand-white/60 text-sm font-body">
            {authMode === 'login' 
              ? t('auth.welcome')
              : t('auth.joinUs')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {authMode === 'register' && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-brand-gold text-sm font-body">{t('auth.fullName')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gold/50" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('auth.fullName')}
                  required
                  className="pl-10 bg-brand-black border-brand-gold/30 text-brand-white placeholder:text-brand-white/40 focus:border-brand-gold rounded-none"
                  data-testid="auth-name-input"
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-brand-gold text-sm font-body">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gold/50" />
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                required
                className="pl-10 bg-brand-black border-brand-gold/30 text-brand-white placeholder:text-brand-white/40 focus:border-brand-gold rounded-none"
                data-testid="auth-email-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-brand-gold text-sm font-body">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gold/50" />
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="pl-10 bg-brand-black border-brand-gold/30 text-brand-white placeholder:text-brand-white/40 focus:border-brand-gold rounded-none"
                data-testid="auth-password-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-brand-gold text-brand-black hover:bg-brand-gold/90 font-bold py-6 uppercase tracking-widest rounded-none mt-6"
            data-testid="auth-submit-btn"
          >
            {loading ? '...' : authMode === 'login' ? t('auth.loginBtn') : t('auth.createAccount')}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center my-6">
          <div className="flex-1 border-t border-brand-gold/30"></div>
          <span className="px-4 text-brand-white/50 text-sm font-body">{t('auth.or')}</span>
          <div className="flex-1 border-t border-brand-gold/30"></div>
        </div>

        {/* Google Login */}
        <Button 
          type="button"
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full border-brand-gold/30 text-brand-white hover:bg-brand-gold/10 hover:border-brand-gold py-6 rounded-none"
          data-testid="google-login-btn"
        >
          <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.continueWithGoogle')}
        </Button>

        {/* Switch mode */}
        <div className="mt-6 text-center">
          {authMode === 'login' ? (
            <p className="text-brand-white/60 text-sm font-body">
              {t('auth.noAccount')}{' '}
              <button onClick={() => setAuthMode('register')} className="text-brand-gold hover:underline font-medium">
                {t('auth.signUp')}
              </button>
            </p>
          ) : (
            <p className="text-brand-white/60 text-sm font-body">
              {t('auth.alreadyAccount')}{' '}
              <button onClick={() => setAuthMode('login')} className="text-brand-gold hover:underline font-medium">
                {t('auth.login')}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
